<template>
  <div class="container text-secondary">
    <div class="row mb-3">
      <div class="col-2"/>
      <div class="col text-center">
        <div class="h4">🃏 Klaverjassen</div>
      </div>
      <div class="col-2 text-end pe-2">
        <img src="@/assets/back-48.png" alt="terug" class="img-fluid"
             style="height:32px; cursor:pointer;" @click="$router.push('/')"/>
      </div>
    </div>

    <hr class="divider">

    <!-- Setup -->
    <div v-if="!gameStarted">
      <div class="card-modern">
        <div style="font-size:0.85rem; color:#718096; line-height:1.7;">
          <strong>Spelregels:</strong><br>
          Per potje zijn er 162 punten te verdelen.<br>
          De spelende partij moet ≥ 82 punten halen (incl. roem).<br>
          Nat = 0 punten, tegenstander krijgt 162.<br>
          Roem wordt bij de eigen score opgeteld vóór de nat-check.
        </div>
      </div>
      <div class="text-center">
        <button class="btn-primary-modern" @click="startGame">Begin</button>
      </div>
    </div>

    <!-- Spel bezig -->
    <div v-else>

      <!-- Huidig potje invoer -->
      <div v-if="!allesPotjesKlaar" class="card-modern">
        <div class="dealer-bar mb-3">
          <span>🃏 Potje {{ huidigPotje }} / {{ aantalPotjes }}</span>
          <span style="color:#667eea; font-size:0.9rem;">
            Speelt: <strong>{{ spelendePartijNaam }}</strong>
          </span>
        </div>

        <!-- 2 of 4 spelers: 2 invoervelden -->
        <div v-if="aantalSpelers !== 3">
          <div v-for="(partij, pi) in partijen" :key="pi" class="invoer-blok mb-3">
            <div style="font-weight:700; color:#4a5568; margin-bottom:8px;">
              {{ partij.naam }}
              <span v-if="pi === spelendIndex" class="speelt-badge">Speelt</span>
            </div>
            <div class="invoer-rij">
              <div class="invoer-veld">
                <label>Punten</label>
                <input type="number" min="0" max="162" class="score-input"
                       v-model.number="huidigInvoer[pi].punten"
                       @input="syncPunten(pi)"
                       placeholder="0"/>
              </div>
              <div class="invoer-veld">
                <label>Roem</label>
                <div class="roem-grid">
                  <button v-for="r in roemWaarden" :key="r"
                          class="roem-btn"
                          :class="{ active: huidigInvoer[pi].roem === r }"
                          @click="huidigInvoer[pi].roem = r">
                    {{ r }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 3 spelers: 3 invoervelden -->
        <div v-else>
          <div v-for="(speler, si) in players" :key="si" class="invoer-blok mb-3">
            <div style="font-weight:700; color:#4a5568; margin-bottom:8px;">
              {{ speler.name }}
              <span v-if="si === spelendIndex" class="speelt-badge">Speelt</span>
            </div>
            <div class="invoer-rij">
              <div class="invoer-veld">
                <label>Punten</label>
                <input type="number" min="0" max="162" class="score-input"
                       v-model.number="huidigInvoer[si].punten"
                       placeholder="0"/>
              </div>
              <div class="invoer-veld">
                <label>Roem</label>
                <div class="roem-grid">
                  <button v-for="r in roemWaarden" :key="r"
                          class="roem-btn"
                          :class="{ active: huidigInvoer[si].roem === r }"
                          @click="huidigInvoer[si].roem = r">
                    {{ r }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-if="invoerFout" style="color:#e53e3e; font-size:0.85rem; margin-bottom:8px; text-align:center;">
          {{ invoerFout }}
        </div>

        <button class="btn-primary-modern" style="width:100%;" @click="bevestigPotje">
          Bevestig potje {{ huidigPotje }}
        </button>
      </div>

      <!-- Spel klaar -->
      <div v-else class="card-modern text-center">
        <div style="font-size:2rem;">🏆</div>
        <div class="h5 mt-2">Spel klaar!</div>
        <div style="margin-top:8px; color:#4a5568;">
          <div v-for="(partij, pi) in partijen" :key="pi">
            {{ partij.naam }}: <strong>{{ eindTotaal(pi) }}</strong> punten
          </div>
        </div>
      </div>

      <!-- Scorebord -->
      <div class="card-modern mt-3">
        <div class="h6 mb-3">Scorebord</div>

        <!-- 2 of 4 spelers: zij aan zij zoals het blok -->
        <div v-if="aantalSpelers !== 3" class="scorebord-klaverjas">
          <table class="klaverjas-table">
            <thead>
            <tr>
              <th class="kj-nr">#</th>
              <th class="kj-naam" colspan="2">{{ partijen[0].naam }}</th>
              <th class="kj-naam" colspan="2">{{ partijen[1].naam }}</th>
            </tr>
            <tr>
              <th class="kj-nr"></th>
              <th class="kj-col">Punten</th>
              <th class="kj-col">Roem</th>
              <th class="kj-col">Punten</th>
              <th class="kj-col">Roem</th>
            </tr>
            </thead>
            <tbody>
            <template v-for="(potje, idx) in gespeeldePotjes" :key="idx">
              <tr :class="{ 'nat-rij': potje.nat !== undefined }">
                <td class="kj-nr">{{ idx + 1 }}</td>
                <td class="kj-col" :class="{ 'nat-cel': potje.nat === 0 }">
                  {{ potje.scores[0].punten }}
                  <span v-if="potje.nat === 0" class="nat-label">NAT</span>
                </td>
                <td class="kj-col">{{ potje.scores[0].roem || '' }}</td>
                <td class="kj-col" :class="{ 'nat-cel': potje.nat === 1 }">
                  {{ potje.scores[1].punten }}
                  <span v-if="potje.nat === 1" class="nat-label">NAT</span>
                </td>
                <td class="kj-col">{{ potje.scores[1].roem || '' }}</td>
              </tr>
              <!-- Subtotaal na elke 4 potjes -->
              <tr v-if="(idx + 1) % 4 === 0 && idx + 1 < aantalPotjes" class="subtotaal-rij">
                <td class="kj-nr" style="font-size:0.75rem; color:#a0aec0;">sub</td>
                <td class="kj-col subtotaal-cel" colspan="2">
                  {{ subtotaalPunten(0, idx) }}
                  <span style="font-size:0.75rem; color:#a0aec0;">
                      (+{{ subtotaalRoem(0, idx) }})
                    </span>
                </td>
                <td class="kj-col subtotaal-cel" colspan="2">
                  {{ subtotaalPunten(1, idx) }}
                  <span style="font-size:0.75rem; color:#a0aec0;">
                      (+{{ subtotaalRoem(1, idx) }})
                    </span>
                </td>
              </tr>
            </template>
            </tbody>
            <tfoot>
            <tr class="roem-rij">
              <td class="kj-nr" style="font-size:0.8rem;">Roem</td>
              <td class="kj-col subtotaal-cel" colspan="2">{{ totaalRoem(0) }}</td>
              <td class="kj-col subtotaal-cel" colspan="2">{{ totaalRoem(1) }}</td>
            </tr>
            <tr class="totaal-rij">
              <td class="kj-nr">Tot</td>
              <td class="kj-col totaal-cel" colspan="2">{{ eindTotaal(0) }}</td>
              <td class="kj-col totaal-cel" colspan="2">{{ eindTotaal(1) }}</td>
            </tr>
            </tfoot>
          </table>
        </div>

        <!-- 3 spelers: gewone tabel -->
        <div v-else class="scorebord-wrapper">
          <table class="sticky-table">
            <thead>
            <tr>
              <th class="col-naam sticky-links">#</th>
              <th v-for="(speler, si) in players" :key="si" colspan="2" class="kj-naam">
                {{ speler.name }}
              </th>
            </tr>
            <tr>
              <th class="col-naam sticky-links"></th>
              <template v-for="(speler, si) in players" :key="si">
                <th class="kj-col">Pnt</th>
                <th class="kj-col">Roem</th>
              </template>
            </tr>
            </thead>
            <tbody>
            <template v-for="(potje, idx) in gespeeldePotjes" :key="idx">
              <tr>
                <td class="col-naam sticky-links">{{ idx + 1 }}</td>
                <template v-for="(speler, si) in players" :key="si">
                  <td class="kj-col" :class="{ 'nat-cel': potje.nat === si }">
                    {{ potje.scores[si].punten }}
                    <span v-if="potje.nat === si" class="nat-label">NAT</span>
                  </td>
                  <td class="kj-col">{{ potje.scores[si].roem || '' }}</td>
                </template>
              </tr>
              <tr v-if="(idx + 1) % 3 === 0 && idx + 1 < aantalPotjes" class="subtotaal-rij">
                <td class="col-naam sticky-links" style="font-size:0.75rem; color:#a0aec0;">sub</td>
                <template v-for="(speler, si) in players" :key="si">
                  <td class="kj-col subtotaal-cel" colspan="2">
                    {{ subtotaalPunten(si, idx) }}
                    <span style="font-size:0.75rem; color:#a0aec0;">(+{{ subtotaalRoem(si, idx) }})</span>
                  </td>
                </template>
              </tr>
            </template>
            </tbody>
            <tfoot>
            <tr class="roem-rij">
              <td class="col-naam sticky-links" style="font-size:0.8rem;">Roem</td>
              <template v-for="(speler, si) in players" :key="si">
                <td class="kj-col subtotaal-cel" colspan="2">{{ totaalRoem(si) }}</td>
              </template>
            </tr>
            <tr class="totaal-rij">
              <td class="col-naam sticky-links">Tot</td>
              <template v-for="(speler, si) in players" :key="si">
                <td class="kj-col totaal-cel" colspan="2">{{ eindTotaal(si) }}</td>
              </template>
            </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import store from "@/store";

export default {
  name: "KlaverjasGame",
  data() {
    return {
      gameStarted: false,
      huidigPotje: 1,
      gespeeldePotjes: [],
      huidigInvoer: [],
      invoerFout: '',
      roemWaarden: [0, 20, 40, 50, 60, 70, 80, 90, 100],
    };
  },
  computed: {
    players() {
      return store.state.playersRoom.length >= 2
          ? store.state.playersRoom
          : store.state.players;
    },
    aantalSpelers() {
      return this.players.length;
    },
    aantalPotjes() {
      if (this.aantalSpelers === 2) return 8;
      if (this.aantalSpelers === 3) return 9;
      return 16;
    },
    // Partijen: bij 4 spelers teams, anders individueel
    partijen() {
      if (this.aantalSpelers === 4) {
        return [
          { naam: `${this.players[0]?.name} & ${this.players[2]?.name}`, indices: [0, 2] },
          { naam: `${this.players[1]?.name} & ${this.players[3]?.name}`, indices: [1, 3] },
        ];
      }
      if (this.aantalSpelers === 2) {
        return [
          { naam: this.players[0]?.name, indices: [0] },
          { naam: this.players[1]?.name, indices: [1] },
        ];
      }
      // 3 spelers: geen partijen, individueel
      return this.players.map((p, i) => ({ naam: p.name, indices: [i] }));
    },
    // Wie speelt dit potje
    spelendIndex() {
      if (this.aantalSpelers === 4) {
        const spelerIndex = (this.huidigPotje - 1) % 4;
        // A(0) en C(2) → team 0 (AC), B(1) en D(3) → team 1 (BD)
        return spelerIndex % 2 === 0 ? 0 : 1;
      }
      return (this.huidigPotje - 1) % this.aantalSpelers;
    },
    spelendePartijNaam() {
      if (this.aantalSpelers === 3) {
        return this.players[this.spelendIndex]?.name;
      }
      return this.partijen[this.spelendIndex]?.naam;
    },
    allesPotjesKlaar() {
      return this.huidigPotje > this.aantalPotjes;
    },
  },
  methods: {
    startGame() {
      this.huidigPotje = 1;
      this.gespeeldePotjes = [];
      this.resetInvoer();
      this.gameStarted = true;
    },
    resetInvoer() {
      const aantal = this.aantalSpelers === 3 ? 3 : 2;
      this.huidigInvoer = Array.from({ length: aantal }, () => ({ punten: 0, roem: 0 }));
      this.invoerFout = '';
    },
    syncPunten(pi) {
      if (this.aantalSpelers !== 3 && this.huidigInvoer[pi].punten >= 0) {
        const andere = pi === 0 ? 1 : 0;
        const ingevoerd = parseInt(this.huidigInvoer[pi].punten) || 0;
        if (ingevoerd <= 162) {
          this.huidigInvoer[andere].punten = 162 - ingevoerd;
        }
      }
    },
    bevestigPotje() {
      this.invoerFout = '';

      if (this.aantalSpelers === 3) {
        this.verwerkPotje3Spelers();
      } else {
        this.verwerkPotje2of4Spelers();
      }
    },
    verwerkPotje2of4Spelers() {
      const spelend = this.spelendIndex;
      const andere = spelend === 0 ? 1 : 0;

      const spelendPunten = parseInt(this.huidigInvoer[spelend].punten) || 0;
      const spelendRoem = this.huidigInvoer[spelend].roem || 0;
      const anderePunten = parseInt(this.huidigInvoer[andere].punten) || 0;
      const andereRoem = this.huidigInvoer[andere].roem || 0;

      const spelendTotaal = spelendPunten + spelendRoem;
      const andereTotaal = anderePunten + andereRoem;

      let scores = [
        { punten: this.huidigInvoer[0].punten, roem: this.huidigInvoer[0].roem || 0 },
        { punten: this.huidigInvoer[1].punten, roem: this.huidigInvoer[1].roem || 0 },
      ];

      let nat = undefined;

      if (spelendTotaal <= andereTotaal) {
        // Nat: spelende partij krijgt 0, tegenstander krijgt 162 + alle roem
        nat = spelend;
        scores[spelend].punten = 0;
        scores[spelend].roem = 0;
        scores[andere].punten = 162;
        scores[andere].roem = andereRoem + spelendRoem;
      }

      this.gespeeldePotjes.push({ scores, nat });
      this.huidigPotje++;
      this.resetInvoer();
    },
    verwerkPotje3Spelers() {
      const spelend = this.spelendIndex;
      const invoer = this.huidigInvoer.map(i => ({
        punten: i.punten,
        roem: i.roem,
        totaal: i.punten + i.roem,
      }));

      const spelendTotaal = invoer[spelend].totaal;
      const anderenMetMeer = [];
      invoer.forEach((inv, i) => {
        if (i !== spelend && inv.totaal > spelendTotaal) {
          anderenMetMeer.push(i);
        }
      });

      let scores = invoer.map(i => ({ punten: i.punten, roem: i.roem }));
      let nat = undefined;

      if (anderenMetMeer.length === 1) {
        nat = spelend;
        const winnaar = anderenMetMeer[0];
        // Punten én roem van spelende gaan naar winnaar
        scores[winnaar].punten += scores[spelend].punten;
        scores[winnaar].roem += scores[spelend].roem;
        scores[spelend].punten = 0;
        scores[spelend].roem = 0;
      } else if (anderenMetMeer.length > 1) {
        nat = spelend;
        const extraPunten = Math.floor(scores[spelend].punten / anderenMetMeer.length);
        const extraRoem = Math.floor(scores[spelend].roem / anderenMetMeer.length);
        anderenMetMeer.forEach(i => {
          scores[i].punten += extraPunten;
          scores[i].roem += extraRoem;
        });
        scores[spelend].punten = 0;
        scores[spelend].roem = 0;
      }

      this.gespeeldePotjes.push({ scores, nat });
      this.huidigPotje++;
      this.resetInvoer();
    },

    // Subtotaal punten t/m potje idx voor partij/speler pi
    subtotaalPunten(pi, totEnMet) {
      return this.gespeeldePotjes
          .slice(0, totEnMet + 1)
          .reduce((sum, p) => sum + (p.scores[pi]?.punten ?? 0), 0);
    },
    subtotaalRoem(pi, totEnMet) {
      return this.gespeeldePotjes
          .slice(0, totEnMet + 1)
          .reduce((sum, p) => sum + (p.scores[pi]?.roem ?? 0), 0);
    },
    totaalRoem(pi) {
      return this.gespeeldePotjes.reduce((sum, p) => sum + (p.scores[pi]?.roem ?? 0), 0);
    },
    eindTotaal(pi) {
      return this.gespeeldePotjes.reduce((sum, p) =>
          sum + (p.scores[pi]?.punten ?? 0) + (p.scores[pi]?.roem ?? 0), 0);
    },
  },
}
</script>

<style scoped>
.dealer-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #f0f4ff;
  border-radius: 8px;
  padding: 6px 10px;
  font-size: 0.9rem;
  color: #4a5568;
}

.speelt-badge {
  background: #667eea;
  color: white;
  border-radius: 8px;
  padding: 2px 8px;
  font-size: 0.75rem;
  margin-left: 6px;
}

.invoer-blok {
  background: #f7fafc;
  border-radius: 12px;
  padding: 12px;
}

.invoer-rij {
  display: flex;
  gap: 12px;
}

.invoer-veld {
  flex: 1;
}

.invoer-veld label {
  display: block;
  font-size: 0.8rem;
  color: #718096;
  margin-bottom: 4px;
}

.score-input {
  width: 100%;
  border: 2px solid #e2e8f0;
  border-radius: 10px;
  padding: 8px 10px;
  font-size: 1.2rem;
  font-weight: 700;
  text-align: center;
  outline: none;
  transition: border-color 0.2s;
}
.score-input:focus { border-color: #667eea; }

.roem-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 4px;
}

.roem-btn {
  background: white;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  padding: 6px 4px;
  font-size: 0.85rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.15s;
  text-align: center;
}
.roem-btn:hover { border-color: #667eea; background: #ebf4ff; }
.roem-btn.active { border-color: #667eea; background: #667eea; color: white; }

/* Klaverjas scorebord */
.scorebord-klaverjas {
  overflow-x: auto;
}

.klaverjas-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9rem;
}

.klaverjas-table th,
.klaverjas-table td {
  padding: 5px 6px;
  text-align: center;
  border-bottom: 1px solid #f0f4f8;
}

.klaverjas-table thead th {
  color: #718096;
  font-weight: 700;
  border-bottom: 2px solid #e2e8f0;
  background: white;
}

.kj-nr {
  width: 28px;
  color: #a0aec0;
  font-size: 0.8rem;
  text-align: center;
}

.kj-naam {
  font-weight: 700;
  color: #2d3748;
  border-bottom: 2px solid #e2e8f0 !important;
}

.kj-col {
  min-width: 44px;
  font-weight: 600;
  color: #2d3748;
}

.nat-cel {
  color: #e53e3e !important;
}

.nat-label {
  font-size: 0.65rem;
  background: #e53e3e;
  color: white;
  border-radius: 4px;
  padding: 1px 3px;
  margin-left: 2px;
}

.subtotaal-rij td {
  background: #f0f4ff;
  font-weight: 700;
}

.subtotaal-cel {
  color: #667eea;
  font-weight: 700;
}

.roem-rij td {
  background: #f7fafc;
  color: #4a5568;
  font-weight: 700;
  border-top: 2px solid #e2e8f0;
}

.totaal-rij td {
  background: white;
  font-weight: 800;
  color: #667eea;
  font-size: 1rem;
  border-top: 2px solid #667eea;
}

/* Sticky tabel voor 3 spelers */
.scorebord-wrapper {
  overflow-x: auto;
  border-radius: 8px;
}

.sticky-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 0.9rem;
}

.sticky-table th,
.sticky-table td {
  padding: 5px 6px;
  text-align: center;
  border-bottom: 1px solid #f0f4f8;
  white-space: nowrap;
  background: white;
}

.sticky-table thead th {
  color: #718096;
  font-weight: 700;
  border-bottom: 2px solid #e2e8f0;
}

.col-naam {
  text-align: left !important;
  font-weight: 600;
  color: #2d3748;
  min-width: 28px;
  position: sticky;
  left: 0;
  z-index: 2;
  box-shadow: 2px 0 4px rgba(0,0,0,0.06);
}
</style>